import React, { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import * as merchantService from "@/shared/api/services/merchantService";
import { motion } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { ArrowDownToLine, ArrowUpFromLine, CheckCircle2, TrendingUp, ArrowRight, Activity, Zap } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import TransactionTable from "@/shared/components/merchant/TransactionTable";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import DateFilter from "@/shared/components/dashboard/DateFilter";
import { getDateRange } from "@/shared/utils/dateFilters";
import { format, subDays, eachDayOfInterval } from "date-fns";
import * as adminService from "@/shared/api/services/adminService";
import * as combinedTransactionService from "@/shared/api/services/combinedTransactionService";
import ChartLoader from "@/shared/components/dashboard/ChartLoader";
import { auth } from "@/shared/api/graphqlClient";

/* ─────────────────────────── STYLES ─────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

.db-root {
  min-height: 100vh;
  font-family: 'Plus Jakarta Sans', sans-serif;
  color: #e2f4ff;
  padding-bottom: 60px;
}
.db-root * { box-sizing: border-box; }

/* ── Hero title ── */
.db-title-main {
  font-size: clamp(1.8rem, 4vw, 2.4rem);
  font-weight: 900; color: #e2f4ff;
  letter-spacing: -0.04em; line-height: 1.05;
}
.db-title-accent {
  background: linear-gradient(135deg, #38bdf8 0%, #22d3ee 40%, #67e8f9 100%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  background-clip: text; display: inline;
}
.db-title-sub { color: #2a4a65; font-size: 13.5px; font-weight: 500; margin-top: 5px; }

/* ── CTA Button ── */
.db-cta {
  display: inline-flex; align-items: center; gap: 9px;
  border: none; border-radius: 14px; padding: 12px 24px;
  color: #fff; font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 13.5px; font-weight: 800; cursor: pointer;
  letter-spacing: 0.01em; position: relative; overflow: hidden;
  background: linear-gradient(135deg, #0ea5e9, #06b6d4, #0891b2);
  box-shadow: 0 6px 32px rgba(14,165,233,0.45), inset 0 1px 0 rgba(255,255,255,0.2);
  transition: box-shadow 0.28s ease, transform 0.28s cubic-bezier(0.34,1.56,0.64,1);
  will-change: transform;
}
.db-cta::before {
  content: ''; position: absolute; top: 0; left: -120%; width: 60%; height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent);
  transform: skewX(-20deg); transition: left 0.65s ease;
}
.db-cta:hover {
  box-shadow: 0 12px 48px rgba(14,165,233,0.6), 0 0 100px rgba(34,211,238,0.2), inset 0 1px 0 rgba(255,255,255,0.25);
  transform: translateY(-3px) scale(1.03);
}
.db-cta:hover::before { left: 160%; }
.db-cta:active { transform: translateY(0) scale(1); }

/* ── Stat cards ── */
.db-stat {
  background: linear-gradient(145deg, rgba(10,22,46,0.96), rgba(8,18,40,0.98));
  border: 1px solid rgba(34,211,238,0.08);
  border-radius: 22px; padding: 22px;
  position: relative; overflow: hidden; cursor: default;
  will-change: transform;
  transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s cubic-bezier(0.34,1.56,0.64,1);
}
.db-stat::before {
  content: ''; position: absolute; top: 0; left: 20%; right: 20%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.25), transparent);
}
.db-stat::after {
  content: ''; position: absolute; top: -20px; right: -20px;
  width: 120px; height: 120px; border-radius: 50%;
  background: radial-gradient(circle, var(--stat-glow, rgba(14,165,233,0.1)), transparent 70%);
  pointer-events: none; transition: opacity 0.3s ease;
  opacity: 0.6;
}
.db-stat:hover {
  border-color: var(--stat-border, rgba(14,165,233,0.3));
  box-shadow: 0 10px 50px var(--stat-shadow, rgba(14,165,233,0.15)), 0 0 0 1px var(--stat-border, rgba(14,165,233,0.15));
  transform: translateY(-6px) scale(1.015);
}
.db-stat:hover::after { opacity: 1; }

.db-stat-icon {
  width: 46px; height: 46px; border-radius: 14px; flex-shrink: 0; margin-bottom: 16px;
  display: flex; align-items: center; justify-content: center;
  position: relative; z-index: 1;
  transition: transform 0.35s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease;
}
.db-stat:hover .db-stat-icon {
  transform: scale(1.18) rotate(-10deg);
  box-shadow: 0 8px 28px var(--stat-shadow, rgba(14,165,233,0.4));
}
.db-stat-icon::after {
  content: ''; position: absolute; inset: 0; border-radius: 14px;
  background: linear-gradient(145deg, rgba(255,255,255,0.12), transparent);
}

.db-stat-lbl {
  font-size: 10px; font-weight: 800; color: #2a4a65;
  text-transform: uppercase; letter-spacing: 0.12em;
  position: relative; z-index: 1; margin-bottom: 6px;
}
.db-stat-val {
  font-family: 'JetBrains Mono', monospace;
  font-size: clamp(1.1rem, 2vw, 1.35rem); font-weight: 700; color: #e2f4ff;
  letter-spacing: -0.02em; position: relative; z-index: 1;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.db-stat-delta {
  display: inline-flex; align-items: center; gap: 4px; margin-top: 6px;
  font-size: 10px; font-weight: 800; position: relative; z-index: 1;
}
.db-stat-delta.up { color: #34d399; }
.db-stat-delta.down { color: #f87171; }
.db-stat-delta.neutral { color: #5a8aaa; }

/* ── Chart card ── */
.db-chart-card {
  background: linear-gradient(145deg, rgba(10,22,46,0.96), rgba(8,18,40,0.98));
  border: 1px solid rgba(34,211,238,0.08); border-radius: 24px; padding: 28px;
  position: relative; overflow: hidden;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}
.db-chart-card::before {
  content: ''; position: absolute; top: 0; left: 10%; right: 10%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.25), transparent);
}
.db-chart-card::after {
  content: ''; position: absolute; top: -60px; right: -60px;
  width: 220px; height: 220px; border-radius: 50%;
  background: radial-gradient(circle, rgba(14,165,233,0.06), transparent 70%);
  pointer-events: none;
}
.db-chart-card:hover {
  border-color: rgba(34,211,238,0.15);
  box-shadow: 0 4px 60px rgba(14,165,233,0.07);
}

.db-chart-title { font-size: 15px; font-weight: 800; color: #e2f4ff; letter-spacing: -0.01em; }
.db-chart-sub { font-size: 12px; color: #2a4a65; margin-top: 3px; font-weight: 500; }

/* ── Section ── */
.db-sec-card {
  background: linear-gradient(145deg, rgba(10,22,46,0.96), rgba(8,18,40,0.98));
  border: 1px solid rgba(34,211,238,0.08); border-radius: 24px; padding: 24px;
  position: relative; overflow: hidden;
}
.db-sec-card::before {
  content: ''; position: absolute; top: 0; left: 15%; right: 15%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.2), transparent);
}

/* ── View all ── */
.db-viewall {
  display: inline-flex; align-items: center; gap: 7px;
  background: rgba(34,211,238,0.07); border: 1px solid rgba(34,211,238,0.18);
  border-radius: 12px; padding: 9px 18px; color: #22d3ee;
  font-family: 'Plus Jakarta Sans', sans-serif; font-size: 12.5px; font-weight: 800; cursor: pointer;
  transition: all 0.28s cubic-bezier(0.34,1.56,0.64,1); position: relative; overflow: hidden;
  will-change: transform;
}
.db-viewall::before {
  content: ''; position: absolute; inset: 0;
  background: rgba(34,211,238,0.08); opacity: 0; transition: opacity 0.2s;
}
.db-viewall:hover { background: rgba(34,211,238,0.12); border-color: rgba(34,211,238,0.4); box-shadow: 0 6px 24px rgba(34,211,238,0.2); transform: translateY(-2px); }
.db-viewall:hover::before { opacity: 1; }
.db-viewall-arrow { transition: transform 0.28s cubic-bezier(0.34,1.56,0.64,1); }
.db-viewall:hover .db-viewall-arrow { transform: translateX(4px); }

/* ── Legend ── */
.db-legend-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

/* ── Responsive ── */
@media (max-width: 900px) { .db-stats-grid { grid-template-columns: repeat(3,1fr) !important; } }
@media (max-width: 600px) {
  .db-stats-grid { grid-template-columns: repeat(2,1fr) !important; gap: 10px !important; }
  .db-header-row { flex-direction: column !important; align-items: flex-start !important; gap: 16px !important; }
  .db-chart-card, .db-sec-card { padding: 18px !important; border-radius: 18px !important; }
  .db-title-main { font-size: 1.6rem; }
}
@media (max-width: 380px) { .db-stats-grid { grid-template-columns: 1fr !important; } }
`;

/* ─────────────────────── ANIMATED COUNTER ─────────────────────── */
function Counter({ to, prefix = "", decimals = 0 }) {
  const [v, setV] = useState(0);
  const num = typeof to === "number" ? to : 0;
  useEffect(() => {
    if (!num) { setV(0); return; }
    let cur = 0; const step = num / 32;
    const id = setInterval(() => {
      cur = Math.min(cur + step, num);
      setV(Math.floor(cur * 10 ** decimals) / 10 ** decimals);
      if (cur >= num) clearInterval(id);
    }, 16);
    return () => clearInterval(id);
  }, [num]);
  if (typeof to === "string") return <>{to}</>;
  return <>{prefix}{v.toLocaleString()}</>;
}

/* ─────────────────────── CHART TOOLTIP ─────────────────────── */
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.92, y: 4 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      style={{
        background: "rgba(6,14,30,0.98)", border: "1px solid rgba(34,211,238,0.2)",
        borderRadius: 16, padding: "14px 20px", fontSize: 13, color: "#e2f4ff",
        boxShadow: "0 24px 64px rgba(0,0,0,0.7)", fontFamily: "'Plus Jakarta Sans', sans-serif",
        backdropFilter: "blur(20px)",
      }}
    >
      <div style={{ color: "#2a4a65", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 10 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ display: "flex", alignItems: "center", gap: 10, margin: "5px 0" }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: p.color, boxShadow: `0 0 10px ${p.color}` }} />
          <span style={{ color: "#5a8aaa", fontSize: 12, fontWeight: 600 }}>{p.name === "deposits" ? "Deposits" : "Withdrawals"}:</span>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", color: p.color, fontWeight: 700 }}>฿{p.value.toLocaleString()}</span>
        </div>
      ))}
    </motion.div>
  );
}

/* ─────────────────────── STAT CARD ─────────────────────── */
const StatCard = ({ config, value, index }) => {
  const [hovered, setHovered] = useState(false);
  const displayVal = typeof value === "number" ? value : 0;

  return (
    <motion.div
      className="db-stat"
      style={{ "--stat-glow": config.glow, "--stat-border": config.border, "--stat-shadow": config.shadow }}
      initial={{ opacity: 0, y: 28, scale: 0.93, filter: "blur(8px)" }}
      animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
      transition={{ delay: index * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      whileHover={{ y: -6 }}
    >
      <div className="db-stat-icon" style={{ background: config.bg }}>
        <config.icon size={19} style={{ color: "#fff", position: "relative", zIndex: 1 }} />
      </div>
      <div className="db-stat-lbl">{config.label}</div>
      <div className="db-stat-val">
        {typeof value === "string" ? value : (
          <Counter to={displayVal} prefix={config.prefix || ""} />
        )}
        {config.suffix && <span>{config.suffix}</span>}
      </div>
      {/* Mini sparkline placeholder bar */}
      <motion.div
        animate={{ opacity: hovered ? 1 : 0.3, scaleX: hovered ? 1 : 0.7 }}
        transition={{ duration: 0.3 }}
        style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: 3,
          background: `linear-gradient(90deg, transparent, ${config.accent}, transparent)`,
          transformOrigin: "left",
        }}
      />
    </motion.div>
  );
};

/* ─────────────────────── QUICK ACTION CARD ─────────────────────── */
const QuickAction = ({ icon: Icon, label, to, color, delay }) => (
  <Link to={createPageUrl(to)} style={{ textDecoration: "none" }}>
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -3, scale: 1.02 }}
      whileTap={{ scale: 0.97 }}
      style={{
        background: "linear-gradient(145deg, rgba(10,22,46,0.96), rgba(8,18,40,0.98))",
        border: "1px solid rgba(34,211,238,0.1)",
        borderRadius: 18, padding: "18px 20px",
        display: "flex", alignItems: "center", gap: 14, cursor: "pointer",
        transition: "border-color 0.25s, box-shadow 0.25s",
        position: "relative", overflow: "hidden",
      }}
      onHoverStart={(e) => { e.target.style && (e.target.style.borderColor = "rgba(34,211,238,0.3)"); }}
    >
      <div style={{
        width: 42, height: 42, borderRadius: 13, flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: color, boxShadow: `0 6px 20px ${color.includes("0ea5") ? "rgba(14,165,233,0.4)" : "rgba(16,185,129,0.4)"}`,
        position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(145deg, rgba(255,255,255,0.12), transparent)" }} />
        <Icon size={18} style={{ color: "#fff", position: "relative", zIndex: 1 }} />
      </div>
      <div>
        <p style={{ color: "#e2f4ff", fontSize: 13, fontWeight: 800 }}>{label}</p>
        <p style={{ color: "#2a4a65", fontSize: 11, fontWeight: 600, marginTop: 2 }}>Quick action</p>
      </div>
      <ArrowRight size={14} style={{ color: "#2a4a65", marginLeft: "auto" }} />
    </motion.div>
  </Link>
);

/* ─────────────────────── STAT CONFIGS ─────────────────────── */
const STAT_CONFIGS = [
  {
    key: "netBalance", label: "Net Balance", icon: CheckCircle2,
    bg: "linear-gradient(135deg, #22d3ee, #0ea5e9)",
    glow: "rgba(34,211,238,0.15)", border: "rgba(34,211,238,0.35)", shadow: "rgba(34,211,238,0.2)",
    accent: "#22d3ee", prefix: "฿",
  },
  {
    key: "feeBalance", label: "Fee Balance", icon: Activity,
    bg: "linear-gradient(135deg, #f43f5e, #fb7185)",
    glow: "rgba(244,63,94,0.15)", border: "rgba(244,63,94,0.35)", shadow: "rgba(244,63,94,0.2)",
    accent: "#fb7185", prefix: "฿",
  },
  {
    key: "todayVolume", label: "Today's Volume", icon: TrendingUp,
    bg: "linear-gradient(135deg, #34d399, #10b981)",
    glow: "rgba(52,211,153,0.15)", border: "rgba(52,211,153,0.35)", shadow: "rgba(52,211,153,0.2)",
    accent: "#34d399", prefix: "฿",
  },
  {
    key: "totalVolume", label: "Total Volume", icon: Activity,
    bg: "linear-gradient(135deg, #0284c7, #0ea5e9)",
    glow: "rgba(2,132,199,0.15)", border: "rgba(2,132,199,0.35)", shadow: "rgba(2,132,199,0.2)",
    accent: "#0284c7", prefix: "฿",
  },
  {
    key: "totalDeposits", label: "Total Deposits", icon: ArrowDownToLine,
    bg: "linear-gradient(135deg, #06b6d4, #67e8f9)",
    glow: "rgba(6,182,212,0.15)", border: "rgba(6,182,212,0.35)", shadow: "rgba(6,182,212,0.2)",
    accent: "#06b6d4", prefix: "฿",
  },
  {
    key: "totalWithdrawals", label: "Total Withdrawals", icon: ArrowUpFromLine,
    bg: "linear-gradient(135deg, #10b981, #34d399)",
    glow: "rgba(16,185,129,0.15)", border: "rgba(16,185,129,0.35)", shadow: "rgba(16,185,129,0.2)",
    accent: "#10b981", prefix: "฿",
  },
  {
    key: "successRate", label: "Success Rate", icon: Zap,
    bg: "linear-gradient(135deg, #f59e0b, #fbbf24)",
    glow: "rgba(245,158,11,0.15)", border: "rgba(245,158,11,0.35)", shadow: "rgba(245,158,11,0.2)",
    accent: "#f59e0b", suffix: "%",
  },
];

/* ─────────────────────── MAIN COMPONENT ─────────────────────── */
export default function MerchantDashboard() {
  const { t } = useLanguage();
  const [selTx, setSelTx] = useState(null);
  const [dateFilter, setDateFilter] = useState("today");
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [time, setTime] = useState(new Date());

  // Live clock
  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const [overviewPage, setOverviewPage] = useState(0);
  const [pageSize] = useState(10);
  const merchantId = auth.getUserData()?.userId;

  const { data: profile } = useQuery({
    queryKey: ["merchant-profile"],
    queryFn: () => merchantService.getProfile()
  });

  const { data: realStats = {} } = useQuery({
    queryKey: ['merchant-stats', merchantId],
    queryFn: async () => adminService.getMerchantDashboardLiveMetrics(merchantId),
    enabled: !!merchantId,
  });

  const { data: combinedQRCodeAndWitData, isLoading: isLoadingQRAndWithdrawals } = useQuery({
    queryKey: ["combined-transactions", overviewPage, pageSize, merchantId],
    queryFn: () => combinedTransactionService.getCombinedQRCodeAndWitData(
      overviewPage + 1,
      pageSize,
      "",
      null,
      [merchantId]
    ),
    enabled: !!merchantId,
  });

  const combinedDataWithdrawalsAndQRCodes = combinedQRCodeAndWitData?.data || [];
  const totalCombinedItems = combinedQRCodeAndWitData?.total || 0;

  const transactions = React.useMemo(() => {
    return combinedDataWithdrawalsAndQRCodes.map((t) => ({
      ...t,
      transaction_id: t.transaction_id || t.payment_id || t.id,
      merchant_ref: t.merchant_ref || t.reference_id,
      type: t.type?.toLowerCase() === 'sale' ? 'deposit' : (t.type?.toLowerCase() || "unknown"),
      status: (t.status || "").toLowerCase(),
      amount: t.amount,
      net_amount: Number(t.net_amount ?? 0),
      total_fee: Number(t.total_fee ?? 0),
      provider_display: t.provider_code || "-",
      currency: t.currency,
      client_bank_name: t.client_bank_name || t.bank_name || t.bank_code,
      created_date: t.created_date || t.createdAt,
      processed_at: t.processed_at || t.updatedAt,
      completed_at: t.processed_at || t.updatedAt,
      is_qr_code: t.type?.toLowerCase() === 'sale' || t.type?.toLowerCase() === 'deposit',
    })).sort((a, b) => new Date(b.created_date) - new Date(a.created_date));
  }, [combinedDataWithdrawalsAndQRCodes]);

  const displayStats = React.useMemo(() => ({
    feeBalance: realStats.feeBalance || 0,
    netBalance: realStats.netBalance || 0,
    totalDeposits: realStats.totalDeposits || 0,
    totalWithdrawals: realStats.totalWithdrawals || 0,
    todayVolume: realStats.todayVolume || 0,
    totalVolume: realStats.totalVolume || 0,
    successRate: realStats.successRate || 0,
  }), [profile, realStats]);

  const dateRange = useMemo(() => {
    return getDateRange(dateFilter, customDateRange);
  }, [dateFilter, customDateRange]);

  const chartDateLabel = useMemo(() => {
    if (!dateRange?.startDate || !dateRange?.endDate) return "Last 7 Days";
    const start = format(dateRange.startDate, "MMM d");
    const end = format(dateRange.endDate, "MMM d");
    return start === end ? start : `${start} - ${end}`;
  }, [dateRange]);

  const { data: chartDataFromQuery = [], isLoading: isChartLoading, isFetching: isChartFetching, refetch: refetchChart } = useQuery({
    queryKey: [
      "merchant-chart",
      merchantId,
      dateRange?.startDate?.toISOString?.(),
      dateRange?.endDate?.toISOString?.(),
    ],
    queryFn: () =>
      adminService.getMerchantChart({
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString(),
        merchantIds: merchantId ? [merchantId] : [],
      }),
    enabled: !!merchantId && !!dateRange?.startDate && !!dateRange?.endDate,
    placeholderData: (previousData) => previousData ?? [],
  });

  const chartData = React.useMemo(() => {
    if (Array.isArray(chartDataFromQuery) && chartDataFromQuery.length > 0) {
      return chartDataFromQuery.map((entry) => ({
        date: entry.name || entry.date || "",
        deposits: Number(entry.deposits || 0),
        withdrawals: Number(entry.withdrawals || 0),
      }));
    }

    if (!dateRange?.startDate || !dateRange?.endDate) {
      return [];
    }

    const days = eachDayOfInterval({ start: dateRange.startDate, end: dateRange.endDate });

    return days.map((day) => {
      const dayTxs = (transactions || []).filter((t) => {
        const created = new Date(t.created_date || t.createdAt || new Date());
        return created.toDateString() === day.toDateString() && t.status === "success";
      });

      return {
        date: format(day, "MMM d"),
        deposits: dayTxs.filter((t) => t.type === "deposit").reduce((sum, t) => sum + (t.amount || 0), 0),
        withdrawals: dayTxs.filter((t) => t.type === "withdrawal").reduce((sum, t) => sum + (t.amount || 0), 0),
      };
    });
  }, [transactions, chartDataFromQuery, dateRange]);

  return (
    <MerchantLayout
      currentPage="MerchantDashboard"
      merchantName={profile?.name || ""}
      merchantCode={profile?.code || ""}
    >
      <style>{CSS}</style>
      <div className="db-root">
        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>

          {/* ── Hero Header ── */}
          <motion.div
            className="db-header-row"
            initial={{ opacity: 0, y: -18, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 18 }}
          >
            <div>
              {/* Live indicator */}
              <motion.div
                initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 }}
                style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}
              >
                <motion.div
                  animate={{ scale: [1, 1.5, 1], opacity: [1, 0.4, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  style={{ width: 7, height: 7, borderRadius: "50%", background: "#34d399" }}
                />
                <span style={{ color: "#2a6a50", fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                  Live · {format(time, "HH:mm:ss")}
                </span>
              </motion.div>

              <div className="db-title-main">
                Merchant <span className="db-title-accent">Dashboard</span>
              </div>
              <p className="db-title-sub">
                {t("welcomeBackOverview") || "Your payment gateway overview"}
              </p>
            </div>

            <div className="gsap-h flex items-center gap-2.5">
              <div className="dash-date-filter">
                <DateFilter
                  value={dateFilter}
                  onChange={setDateFilter}
                  customDateRange={customDateRange}
                  onCustomDateRangeChange={setCustomDateRange}
                />
              </div>
            </div>
          </motion.div>

          {/* ── Stats Grid ── */}
          <div className="db-stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14 }}>
            {STAT_CONFIGS.map((config, i) => (
              <StatCard
                key={config.key}
                config={config}
                value={displayStats[config.key]}
                index={i}
              />
            ))}
          </div>

          {/* ── Chart + Quick Actions Row ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 18, alignItems: "start" }} className="db-chart-row">
            {/* Chart */}
            <motion.div
              className="db-chart-card"
              initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ delay: 0.32, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 14, marginBottom: 28, position: "relative", zIndex: 1 }}>
                <div>
                  <div className="db-chart-title">{t("transactionVolume") || "Transaction Volume"}</div>
                  <div className="db-chart-sub">{`Deposits & Withdrawals · ${chartDateLabel}`}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                  {[{ color: "#0ea5e9", label: t("deposits") || "Deposits" }, { color: "#22d3ee", label: t("withdrawals") || "Withdrawals" }].map(({ color, label }) => (
                    <div key={label} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                      <div className="db-legend-dot" style={{ background: color, boxShadow: `0 0 10px ${color}80` }} />
                      <span style={{ fontSize: 12, color: "#3a5a78", fontWeight: 600 }}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ height: 280, position: "relative", zIndex: 1 }}>
                {isChartLoading || isChartFetching ? (
                  <ChartLoader />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gradDep" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gradWit" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.22} />
                          <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} />
                        </linearGradient>
                        <filter id="glow">
                          <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                          <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
                        </filter>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(34,211,238,0.04)" />
                      <XAxis dataKey="date" axisLine={false} tickLine={false}
                        tick={{ fill: "#1e3a55", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600 }} />
                      <YAxis axisLine={false} tickLine={false}
                        tick={{ fill: "#1e3a55", fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                        tickFormatter={v => v >= 1000 ? `฿${(v / 1000).toFixed(0)}K` : `฿${v}`} />
                      <Tooltip content={<ChartTooltip />} cursor={{ stroke: "rgba(34,211,238,0.12)", strokeWidth: 1 }} />
                      <Area type="monotone" dataKey="deposits" name="deposits" stroke="#0ea5e9" strokeWidth={2.5}
                        fill="url(#gradDep)" dot={false}
                        activeDot={{ r: 6, fill: "#0ea5e9", stroke: "rgba(6,14,30,0.98)", strokeWidth: 2.5, filter: "url(#glow)" }} />
                      <Area type="monotone" dataKey="withdrawals" name="withdrawals" stroke="#22d3ee" strokeWidth={2.5}
                        fill="url(#gradWit)" dot={false}
                        activeDot={{ r: 6, fill: "#22d3ee", stroke: "rgba(6,14,30,0.98)", strokeWidth: 2.5, filter: "url(#glow)" }} />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </motion.div>

            {/* Side panel */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Success Rate Ring */}
              <motion.div
                className="db-chart-card"
                initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.38, duration: 0.45 }}
                style={{ padding: "22px", textAlign: "center" }}
              >
                <div style={{ position: "relative", width: 100, height: 100, margin: "0 auto 14px" }}>
                  <svg viewBox="0 0 100 100" style={{ width: "100%", height: "100%", transform: "rotate(-90deg)" }}>
                    <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(34,211,238,0.08)" strokeWidth="8" />
                    <motion.circle
                      cx="50" cy="50" r="42" fill="none" stroke="#22d3ee" strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 42}`}
                      initial={{ strokeDashoffset: 2 * Math.PI * 42 }}
                      animate={{ strokeDashoffset: 2 * Math.PI * 42 * (1 - displayStats.successRate / 100) }}
                      transition={{ delay: 0.5, duration: 1.2, ease: [0.34, 1.56, 0.64, 1] }}
                      style={{ filter: "drop-shadow(0 0 8px rgba(34,211,238,0.6))" }}
                    />
                  </svg>
                  <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontSize: "1.4rem", fontWeight: 900, color: "#e2f4ff", fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>{displayStats.successRate}%</span>
                  </div>
                </div>
                <p style={{ color: "#e2f4ff", fontSize: 13, fontWeight: 800 }}>Success Rate</p>
                <p style={{ color: "#2a4a65", fontSize: 11, marginTop: 3, fontWeight: 600 }}>{totalCombinedItems} total transactions</p>
              </motion.div>

              {/* Quick Actions */}
              <QuickAction icon={ArrowDownToLine} label="New Deposit" to="MerchantDeposit" color="linear-gradient(135deg, #0ea5e9, #0284c7)" delay={0.42} />
              <QuickAction icon={ArrowUpFromLine} label="New Withdrawal" to="MerchantWithdrawals" color="linear-gradient(135deg, #10b981, #059669)" delay={0.46} />
            </div>
          </div>

          {/* ── Recent Transactions ── */}
          <motion.div
            className="db-sec-card"
            initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ delay: 0.42, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 11, flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: "linear-gradient(135deg, #06b6d4, #0891b2)",
                  boxShadow: "0 4px 16px rgba(6,182,212,0.35)",
                }}>
                  <Activity size={16} style={{ color: "#fff" }} />
                </div>
                <div>
                  <p style={{ color: "#e2f4ff", fontSize: 14, fontWeight: 800 }}>{t("recentTransactions") || "Recent Transactions"}</p>
                  <p style={{ color: "#2a4a65", fontSize: 11, fontWeight: 600 }}>{totalCombinedItems} records found</p>
                </div>
              </div>
            </div>
            <TransactionTable transactions={transactions.slice(0, 5)} isLoading={isLoadingQRAndWithdrawals} onRowClick={setSelTx} />
          </motion.div>

        </div>
      </div >
      <style>{`
        @media (max-width: 900px) { .db-chart-row { grid-template-columns: 1fr !important; } }
      `}</style>
    </MerchantLayout >
  );
}